import {
  FieldValues,
} from 'react-hook-form';
import transform from 'lodash/transform';
import {
  DateTimeRangeFilterDTO,
  DecimalNullableRangeFilterDTO,
  FilterLookupTranslatorDTO,
  FilterableSettingDTO,
  FilterableOptionDTO,
} from 'dtos';
import {
  DateRange,
} from 'types/dateRange';
import {
  FilterableSettings,
  FilterComponentsType,
  FilterLookupTranslator,
  FilterObjectType,
} from 'core/Filters/types';
import {
  LookupOptionType,
} from 'core/components/Lookup/Generic';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import dayjs from 'utils/dayjs';
import {
  formatISODateTime,
} from 'utils/date';
import omitBy from 'lodash/omitBy';
import isBoolean from 'lodash/isBoolean';

export function isFilterEmpty<T>(filterValues?: T) {
  return isEmpty(filterValues)
   || values(filterValues).every(
     (filterValue) => (isBoolean(filterValue)
       ? filterValue === false
       : (isEmpty(filterValue)
      || (isObject(filterValue) && values(filterValue).every(isNil)))),
   );
}

export const removeEmptyFilterValues = <T extends FieldValues>(filterValues: T) => omitBy(
  filterValues,
  (value) => isFilterEmpty({ value }),
);

const defaultFilterValuesByType: Record<
  FilterComponentsType,
  DecimalNullableRangeFilterDTO | string[] | Partial<DateRange> | string | boolean> = {
    range: {
      min: null,
      max: null,
    },
    multiselect: [],
    lookup: [],
    daterange: {
      min: undefined,
      max: undefined,
    },
    date: '',
    month: '',
    year: '',
    select: '',
    checkbox: false,
  };

const deserializeDateRange = (
  value?: Partial<DateRange> | Partial<DateTimeRangeFilterDTO>,
) => {
  const minValue = value?.min;
  const maxValue = value?.max;
  if (isNil(minValue) || isNil(maxValue)) {
    return value;
  }

  const min = dayjs(minValue);
  const max = dayjs(maxValue);
  if (!min.isValid() || !max.isValid()) {
    return value;
  }

  return {
    min: min.toDate(),
    max: max.toDate(),
  };
};

const deserializeFilterValues = (
  values: FieldValues,
  components: FilterableSettings[],
) => {
  const componentsByName = keyBy(components, 'name');
  return transform(
    values,
    (result, value, key) => {
      const type = componentsByName[key]?.filterType as FilterComponentsType;
      result[key] = type === 'daterange' ? deserializeDateRange(value) : value;
    },
    {} as FieldValues,
  );
};

export const getFilterDefaultValues = (
  settings: Record<string, any>,
  pageObject: FilterObjectType,
  components: FilterableSettings[],
  storageValue: FieldValues | null = null,
) => {
  const values = storageValue
    ?? settings[`${pageObject}Filters`]
    ?? {};

  return deserializeFilterValues(values, components);
};

const serializeRange = (value: DecimalNullableRangeFilterDTO) => ({
  min: isNil(value?.min) ? null : Number(value?.min),
  max: isNil(value?.max) ? null : Number(value?.max),
});

const serializeLookup = (
  values: LookupOptionType[],
  lookupTranslator?: FilterLookupTranslator,
) => {
  const { idField = '', displayNameField = '' } = lookupTranslator ?? {};
  return (values ?? []).map((option) => ({
    [idField ?? '']: option?.[idField ?? ''],
    [displayNameField ?? '']: option?.[displayNameField ?? ''],
  }));
};

const serializeMultiselect = (value: string[] | number[]) => value;
const serializeDateRange = (value?: DateRange | null): DateTimeRangeFilterDTO | undefined => {
  if (isNil(value?.min) || isNil(value?.max)) {
    return undefined;
  }

  const { min, max } = value;
  const [serializedMin, serializedMax] = [
    dayjs(min).startOf('day'),
    dayjs(max).endOf('day'),
  ].map((date) => formatISODateTime(date));

  return { min: serializedMin, max: serializedMax };
};

const serializeDate = (date: string) => (
  formatISODateTime(dayjs(date).startOf('day'))
);
const serializeMonth = (date: string) => (
  formatISODateTime(dayjs(date).startOf('month').startOf('day'))
);
const serializeYear = (date: string) => (
  formatISODateTime(dayjs(date).startOf('year').startOf('day'))
);
const serializeSelect = (option: FilterableOptionDTO) => option?.value;
const serializeCheckboxValues = (value: boolean) => value;

const serializeFunctionByType: Record<FilterComponentsType, (...values: any) => any> = {
  range: serializeRange,
  lookup: serializeLookup,
  multiselect: serializeMultiselect,
  daterange: serializeDateRange,
  date: serializeDate,
  month: serializeMonth,
  year: serializeYear,
  select: serializeSelect,
  checkbox: serializeCheckboxValues,
};

export const serializeFilterQuery = (
  values: FieldValues,
  components: FilterableSettings[],
) => {
  const componentsByName = keyBy(components, 'name');
  return transform(
    values,
    (result, value, key) => {
      const metadata = componentsByName[key];
      const lookupTranslator = metadata?.lookupTranslator ?? {};
      const type = (metadata?.filterType ?? '') as FilterComponentsType;
      const serializedValue = serializeFunctionByType[type]?.(value, lookupTranslator);
      if (type === 'daterange' && isNil(serializedValue)) {
        return;
      }
      result[key] = serializedValue;
    },
    {} as FieldValues,
  );
};

export const getFilterInitialValues = (
  values: FieldValues,
  components: FilterableSettings[],
) => {
  const componentsByName = keyBy(components, 'name');
  return transform(values, (result, value, key) => {
    const type = componentsByName[key]?.filterType as FilterComponentsType;
    result[key] = defaultFilterValuesByType[type];
  }, {} as FieldValues);
};

export const normalizeFilterableSettings = (
  filterableSettings: FilterableSettingDTO[],
): FilterableSettings[] => filterableSettings
  .filter((setting) => !isNil(setting.dtoName))
  .map((setting) => ({
    ...setting,
    name: setting.dtoName!,
    lookupTranslator: omitBy(setting.lookupTranslator as FilterLookupTranslatorDTO, isNil),
  }));
